import React, { useState, useEffect, useCallback } from 'react';
import { ChatBubbleLeftRightIcon, UserGroupIcon, FunnelIcon, ChartBarIcon, AcademicCapIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import analyticsService, { ConversionFunnelData } from '../../services/analyticsService';
import ConversationBrowser from './ConversationBrowser';
import ConversionFunnel from './ConversionFunnel';
import DropOffAnalysis from './DropOffAnalysis';
import QuizAnalytics from './QuizAnalytics';
import EmailHealthCard from './EmailHealthCard';

const AnalyticsDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState<any>(null);
  const [funnel, setFunnel] = useState<ConversionFunnelData | null>(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('last30Days');

  const tabs = [
    { id: 'overview', name: 'Overview', icon: ChartBarIcon },
    { id: 'conversations', name: 'All Conversations', icon: ChatBubbleLeftRightIcon },
    { id: 'funnel', name: 'Conversion Funnel', icon: FunnelIcon },
    { id: 'dropoff', name: 'Drop-off Analysis', icon: UserGroupIcon },
    { id: 'quiz', name: 'Quiz Analytics', icon: AcademicCapIcon },
  ];

  const dateRanges = [
    { id: 'today', label: 'Today' },
    { id: 'last7Days', label: 'Last 7 Days' },
    { id: 'last30Days', label: 'Last 30 Days' },
    { id: 'last90Days', label: 'Last 90 Days' },
    { id: 'allTime', label: 'All Time' }
  ];

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const ranges = analyticsService.getPresetDateRanges();
      const currentRange = ranges[dateRange as keyof typeof ranges];

      const [statsData, funnelData] = await Promise.all([
        analyticsService.getConversationStats(currentRange),
        analyticsService.getConversionFunnel(currentRange)
      ]);

      setStats(statsData);
      setFunnel(funnelData);
    } catch (error) {
      console.error('Failed to fetch analytics data:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const renderOverview = () => {
    if (!stats || !funnel) return null;

    const overviewCards = [
      {
        title: 'Total Conversations',
        value: stats.overview.total_conversations,
        icon: '💬',
        color: 'bg-blue-500',
        subtitle: `${stats.overview.unique_visitors} unique visitors`
      },
      {
        title: 'Conversion Rate',
        value: `${stats.overview.conversion_rate}%`,
        icon: '📈',
        color: 'bg-green-500',
        subtitle: `${stats.overview.converted_conversations} converted`
      },
      {
        title: 'Abandoned Conversations',
        value: stats.overview.abandoned_conversations,
        icon: '❌',
        color: 'bg-red-500',
        subtitle: `${((stats.overview.abandoned_conversations / stats.overview.total_conversations) * 100).toFixed(1)}% drop-off`
      },
      {
        title: 'Avg. Duration',
        value: `${stats.overview.avg_duration_minutes}m`,
        icon: '⏱️',
        color: 'bg-purple-500',
        subtitle: `${stats.overview.avg_messages_per_conversation} messages avg`
      },
      {
        title: 'Active Conversations',
        value: stats.overview.active_conversations,
        icon: '🔄',
        color: 'bg-orange-500',
        subtitle: 'Currently ongoing'
      },
      {
        title: 'Overall Conversion',
        value: `${funnel.conversion_rates.overall_conversion}%`,
        icon: '🎯',
        color: 'bg-teal-500',
        subtitle: 'Visitors to leads'
      }
    ];

    return (
      <div className="space-y-6">
        {/* Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {overviewCards.map((card, index) => (
            <div key={index} className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center">
                <div className={`${card.color} rounded-lg p-3 text-white text-2xl mr-4`}>
                  {card.icon}
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">{card.title}</p>
                  <p className="text-3xl font-bold text-gray-900">{card.value}</p>
                  <p className="text-xs text-gray-500 mt-1">{card.subtitle}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Email Health Card */}
        <EmailHealthCard />

        {/* Quick Stats and Trends */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Trends */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Trends (Last 7 Days)</h3>
            {stats.trends.length > 0 ? (
              <div className="space-y-3">
                {stats.trends.map((trend: any, index: number) => (
                  <div key={index} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
                    <div>
                      <p className="font-medium text-gray-900">{new Date(trend.date).toLocaleDateString()}</p>
                      <p className="text-sm text-gray-600">{trend.conversations} conversations</p>
                    </div>
                    <div className="text-right">
                      <div className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        parseFloat(trend.conversion_rate) >= 20
                          ? 'bg-green-100 text-green-800'
                          : parseFloat(trend.conversion_rate) >= 10
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {trend.conversion_rate}% conversion
                      </div>
                      <p className="text-xs text-gray-500 mt-1">{trend.conversions} leads</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">No recent trends available</p>
            )}
          </div>

          {/* Peak Hours */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Peak Activity Hours</h3>
            {stats.peak_hours.length > 0 ? (
              <div className="space-y-3">
                {stats.peak_hours.map((hour: any, index: number) => (
                  <div key={index} className="flex items-center justify-between py-2">
                    <div>
                      <p className="font-medium text-gray-900">{hour.display_hour}</p>
                      <p className="text-sm text-gray-600">Peak #{index + 1}</p>
                    </div>
                    <div className="text-sm text-gray-900 font-medium">
                      {hour.conversations} conversations
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">No peak hours data available</p>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-blue-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-3">Quick Actions</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              onClick={() => setActiveTab('conversations')}
              className="p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow text-left"
            >
              <div className="text-2xl mb-2">💬</div>
              <h4 className="font-semibold text-gray-900">Browse All Conversations</h4>
              <p className="text-sm text-gray-600">View all conversations, including abandoned ones</p>
            </button>
            <button
              onClick={() => setActiveTab('funnel')}
              className="p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow text-left"
            >
              <div className="text-2xl mb-2">📊</div>
              <h4 className="font-semibold text-gray-900">Analyze Conversion Funnel</h4>
              <p className="text-sm text-gray-600">See visitor journey from start to lead</p>
            </button>
            <button
              onClick={() => setActiveTab('dropoff')}
              className="p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow text-left"
            >
              <div className="text-2xl mb-2">📉</div>
              <h4 className="font-semibold text-gray-900">Drop-off Analysis</h4>
              <p className="text-sm text-gray-600">Identify where visitors abandon conversations</p>
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverview();
      case 'conversations':
        return <ConversationBrowser />;
      case 'funnel':
        return <ConversionFunnel />;
      case 'dropoff':
        return <DropOffAnalysis />;
      case 'quiz':
        return <QuizAnalytics />;
      default:
        return renderOverview();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Conversation Analytics</h2>
            <p className="text-gray-600 mt-1">
              Track visitor journeys, conversion rates, and drop-off points
            </p>
          </div>
          <div className="mt-4 sm:mt-0">
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
            >
              {dateRanges.map((range) => (
                <option key={range.id} value={range.id}>
                  {range.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-md">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="h-5 w-5 mr-2" />
                  {tab.name}
                </button>
              );
            })}
          </nav>
        </div>
        <div className="p-6">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;